import type { DayHours, OpenStatus, ShopInfo, Weekday } from './types';

/** Format an integer cent amount as USD, e.g. 450 → "$4.50". Never hand-typed. */
export function formatPrice(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

/** "07:00" → minutes since midnight (420). */
function parseHm(value: string): number {
  const [h, m] = value.split(':').map(Number);
  return h * 60 + m;
}

/** 420 → "7:00 AM". */
function formatHm(minutes: number): string {
  const h24 = Math.floor(minutes / 60);
  const m = minutes % 60;
  const period = h24 < 12 ? 'AM' : 'PM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${m.toString().padStart(2, '0')} ${period}`;
}

function formatRange(hours: DayHours): string {
  return `${formatHm(parseHm(hours.open))} – ${formatHm(parseHm(hours.close))}`;
}

/**
 * Compute open/closed status from shop hours + the current time.
 *
 * Derived, never a stored copy. Accessibility note: callers must not rely on
 * color alone — the returned `label` always carries the status in words.
 */
export function computeOpenStatus(
  hours: ShopInfo['hours'],
  now: Date = new Date(),
): OpenStatus {
  const today = now.getDay() as Weekday;
  const tomorrow = ((today + 1) % 7) as Weekday;
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  const todayHours = hours[today];

  if (todayHours) {
    const openM = parseHm(todayHours.open);
    const closeM = parseHm(todayHours.close);

    if (nowMinutes < openM) {
      return {
        isOpen: false,
        label: `Closed · Opens at ${formatHm(openM)}`,
        todayLabel: formatRange(todayHours),
      };
    }
    if (nowMinutes >= openM && nowMinutes < closeM) {
      return {
        isOpen: true,
        label: `Open now · until ${formatHm(closeM)}`,
        todayLabel: formatRange(todayHours),
      };
    }
  }

  // Closed for the rest of today — find the next day that opens.
  for (let i = 1; i <= 7; i++) {
    const day = ((today + i) % 7) as Weekday;
    const dayHours = hours[day];
    if (dayHours) {
      const when = day === tomorrow ? 'tomorrow' : weekdayName(day);
      return {
        isOpen: false,
        label: `Closed · Opens ${when} at ${formatHm(parseHm(dayHours.open))}`,
        todayLabel: todayHours ? formatRange(todayHours) : 'Closed today',
      };
    }
  }

  return { isOpen: false, label: 'Closed', todayLabel: 'Closed' };
}

const WEEKDAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

export function weekdayName(day: Weekday): string {
  return WEEKDAY_NAMES[day];
}

/** Today's formatted hours range, or "Closed". */
export function todayHoursLabel(
  hours: ShopInfo['hours'],
  now: Date = new Date(),
): string {
  const todayHours = hours[now.getDay() as Weekday];
  return todayHours ? formatRange(todayHours) : 'Closed';
}
